import { format, startOfWeek, endOfWeek } from "date-fns";
import { ptBR } from "date-fns/locale";
import type { AppointmentWithDetails } from "@/hooks/useAppointments";
import { exportToExcel } from "@/lib/utils/export";
import {
  getEffectiveAppointmentDriverType,
  type AppointmentDriverType,
} from "@/lib/utils/appointment-driver";
import {
  getPrimaryOwnerAddress,
  type OwnerAddress,
} from "@/lib/appointments/owner-address";

type OwnerPhone = {
  phone_number?: string | null;
  is_primary?: boolean | null;
  deleted_at?: string | null;
};

type OwnerEmail = {
  email?: string | null;
  is_primary?: boolean | null;
  deleted_at?: string | null;
};

function formatAddressRua(addr: OwnerAddress | null): string {
  if (!addr) return "";
  return (addr.street?.trim() ?? "").trim();
}

function formatAddressNumero(addr: OwnerAddress | null): string {
  if (!addr) return "";
  return (addr.number?.trim() ?? "").trim();
}

function formatAddressComplemento(addr: OwnerAddress | null): string {
  if (!addr) return "";
  return (addr.complement?.trim() ?? "").trim();
}

function formatAddressBairro(addr: OwnerAddress | null): string {
  if (!addr) return "";
  return (addr.neighborhood?.trim() ?? "").trim();
}

function formatAddressCidade(addr: OwnerAddress | null): string {
  if (!addr?.city) return "";
  return (addr.city.name?.trim() ?? "").trim();
}

function formatAddressCep(addr: OwnerAddress | null): string {
  if (!addr) return "";
  return (addr.zip_code?.trim() ?? "").trim();
}

function formatAddressSingleLine(addr: OwnerAddress | null): string {
  if (!addr) return "";
  const street = formatAddressRua(addr);
  const number = formatAddressNumero(addr);
  const complement = formatAddressComplemento(addr);
  const neighborhood = formatAddressBairro(addr);
  const city = formatAddressCidade(addr);
  const state = (addr.city?.state?.code?.trim() ?? "").trim();
  const cep = formatAddressCep(addr);

  const line1 = [street, number ? `nº ${number}` : "", complement].filter(Boolean).join(", ");
  const line2 = [neighborhood, [city, state].filter(Boolean).join(" - "), cep ? `CEP ${cep}` : ""]
    .filter(Boolean)
    .join(" | ");
  return [line1, line2].filter(Boolean).join(" - ");
}

function getAllPhones(appointment: AppointmentWithDetails): string {
  const phones = ((appointment?.owner as unknown as { phones?: OwnerPhone[] } | null | undefined)
    ?.phones ?? [])
    .filter((phone) => !phone?.deleted_at)
    .sort((left, right) => Number(Boolean(right?.is_primary)) - Number(Boolean(left?.is_primary)));

  return phones.map((phone) => phone?.phone_number?.trim() ?? "").filter(Boolean).join("; ");
}

function getPrimaryEmail(appointment: AppointmentWithDetails): string {
  const emails = ((appointment?.owner as unknown as { emails?: OwnerEmail[] } | null | undefined)
    ?.emails ?? []).filter((email) => !email?.deleted_at);
  const primary = emails.find((e) => e?.is_primary) ?? emails[0];
  return primary?.email?.trim() ?? "";
}

function getPetsLabel(appointment: AppointmentWithDetails): string {
  const pets = appointment.pets ?? (appointment.pet ? [appointment.pet] : []);
  const names = pets.map((p: { name?: string | null }) => p?.name?.trim() ?? "").filter(Boolean);
  return names.join("; ");
}

function formatExportEnumLabel(value: string | null | undefined): string {
  if (!value) return "";

  switch (value) {
    case "banho_tosa":
      return "Banho & Tosa";
    case "aplicacao":
      return "Aplicação";
    default:
      break;
  }

  return value
    .trim()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase())
    .join(" ");
}

function formatDriverTypeLabel(driverType: AppointmentDriverType | null): string {
  switch (driverType) {
    case "pickup":
      return "Buscar";
    case "dropoff":
      return "Levar";
    case "pickup_and_dropoff":
      return "Buscar e Levar";
    default:
      return "";
  }
}

/**
 * Builds export rows from appointments (sorted by starts_at).
 * Columns: Data, Hora, Responsável, Telefone(s), E-mail, Rua, Número, Complemento, Bairro, Cidade, CEP, Motorista, Pet(s), Tipo, Status, Status Financeiro, Produtos, Serviços/Pacotes, Observações.
 */
export function buildAppointmentsExportRows(appointments: AppointmentWithDetails[]): Record<string, string>[] {
  const sorted = [...appointments].sort(
    (a, b) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime()
  );

  return sorted.map((apt) => {
    const startsAt = new Date(apt.starts_at);
    const primaryAddr = getPrimaryOwnerAddress(apt);
    const productsAndServices = (apt.appointment_items ?? [])
      .filter((item) => {
        const type = (item.item?.type ?? "").toUpperCase();
        return type === "PRODUTO" || type === "SERVICO";
      })
      .map((item) => item.title_snapshot?.trim() || item.item?.name?.trim() || "")
      .filter(Boolean)
      .join("; ");

    const packageNames = (apt.appointment_patient_packages ?? [])
      .map((link) =>
        link.patient_package?.catalog_package?.item?.name?.trim() ||
        link.catalog_package?.item?.name?.trim() ||
        "",
      )
      .filter(Boolean)
      .join("; ");

    return {
      Data: format(startsAt, "dd/MM/yyyy", { locale: ptBR }),
      Hora: format(startsAt, "HH:mm", { locale: ptBR }),
      "Pet(s)": getPetsLabel(apt),
      "Responsável": (apt.owner?.name as string | undefined)?.trim() ?? "",
      Endereço: formatAddressSingleLine(primaryAddr),
      "Telefone(s)": getAllPhones(apt),
      "E-mail": getPrimaryEmail(apt),
      Tipo: apt.clinic_appointment_type?.name ?? formatExportEnumLabel(apt.type),
      Status: formatExportEnumLabel(apt.status),
      "Produtos/Serviços": productsAndServices,
      Pacotes: packageNames,
      Motorista: formatDriverTypeLabel(getEffectiveAppointmentDriverType({
        driverType: apt.taxi_dog_type,
        isTaxiDog: apt.is_taxi_dog,
      })),
      "Observações": (apt.notes?.trim() ?? "").trim(),
    };
  });
}

export const APPOINTMENT_EXPORT_COLUMN_ORDER = [
  "Data",
  "Hora",
  "Pet(s)",
  "Responsável",
  "Endereço",
  "Telefone(s)",
  "E-mail",
  "Tipo",
  "Status",
  "Produtos/Serviços",
  "Pacotes",
  "Motorista",
  "Observações",
] as const;

export type AppointmentExportColumn = (typeof APPOINTMENT_EXPORT_COLUMN_ORDER)[number];

export const DEFAULT_APPOINTMENT_EXPORT_COLUMNS: AppointmentExportColumn[] = [
  ...APPOINTMENT_EXPORT_COLUMN_ORDER,
];

export function selectAppointmentsExportColumns(
  rows: Record<string, string>[],
  selectedColumns: AppointmentExportColumn[],
): Record<string, string>[] {
  if (rows.length === 0) return [];
  const effectiveColumns = selectedColumns.length > 0 ? selectedColumns : DEFAULT_APPOINTMENT_EXPORT_COLUMNS;

  return rows.map((row) => {
    const out: Record<string, string> = {};
    for (const column of effectiveColumns) {
      out[column] = row[column] ?? "";
    }
    return out;
  });
}

export function getDefaultAppointmentsExportRows(appointments: AppointmentWithDetails[]): Record<string, string>[] {
  const rows = buildAppointmentsExportRows(appointments);
  return selectAppointmentsExportColumns(rows, DEFAULT_APPOINTMENT_EXPORT_COLUMNS);
}

export type AppointmentsCsvViewContext =
  | "day"
  | "week"
  | "month"
  | "flowboard"
  | "grooming";

/**
 * Builds CSV filename from current view context (e.g. dia X, semana Y, mês Z, flowboard, grooming).
 */
export function getAppointmentsCsvFilename(params: {
  view: AppointmentsCsvViewContext;
  date: Date;
}): string {
  const { view, date } = params;
  switch (view) {
    case "day":
      return `agendamentos-dia-${format(date, "yyyy-MM-dd")}.xlsx`;
    case "week": {
      const start = startOfWeek(date, { locale: ptBR });
      const end = endOfWeek(date, { locale: ptBR });
      return `agendamentos-semana-${format(start, "yyyy-MM-dd")}-a-${format(end, "yyyy-MM-dd")}.xlsx`;
    }
    case "month":
      return `agendamentos-mes-${format(date, "MMMM-yyyy", { locale: ptBR })}.xlsx`;
    case "flowboard":
      return `agendamentos-fluxo-de-atendimentos-${format(date, "yyyy-MM-dd")}.xlsx`;
    case "grooming":
      return `agendamentos-banho-tosa-${format(date, "yyyy-MM-dd")}.xlsx`;
    default:
      return `agendamentos-${format(date, "yyyy-MM-dd-HHmm")}.xlsx`;
  }
}

function getAppointmentsDateRange(
  appointments: AppointmentWithDetails[]
): { min: Date; max: Date } | null {
  if (appointments.length === 0) return null;
  let min = new Date(appointments[0].starts_at).getTime();
  let max = min;
  for (let i = 1; i < appointments.length; i++) {
    const t = new Date(appointments[i].starts_at).getTime();
    if (t < min) min = t;
    if (t > max) max = t;
  }
  return { min: new Date(min), max: new Date(max) };
}

/**
 * Triggers download of appointments as XLSX file.
 * Default filename: agendamentos-{minDate}-a-{maxDate}.xlsx when appointments exist.
 */
export function downloadAppointmentsCsv(
  appointments: AppointmentWithDetails[],
  filename?: string,
  selectedColumns?: AppointmentExportColumn[],
): void {
  const baseRows = buildAppointmentsExportRows(appointments);
  const rows = selectAppointmentsExportColumns(baseRows, selectedColumns ?? DEFAULT_APPOINTMENT_EXPORT_COLUMNS);
  const range = getAppointmentsDateRange(appointments);
  const defaultFilename =
    range != null
      ? `agendamentos-${format(range.min, "yyyy-MM-dd")}-a-${format(range.max, "yyyy-MM-dd")}.xlsx`
      : `agendamentos-${format(new Date(), "yyyy-MM-dd-HHmm")}.xlsx`;

  exportToExcel({
    rows,
    sheetName: "Agendamentos",
    fileName: filename ?? defaultFilename,
  });
}
